/** Single pin shape from a known icon set (MIT / Apache licensed sources). */

export type PinGlyphElement =
  | { readonly kind: 'path'; readonly d: string; readonly fillRule?: 'evenodd' | 'nonzero' }
  | {
      readonly kind: 'line';
      readonly x1: number;
      readonly y1: number;
      readonly x2: number;
      readonly y2: number;
    };

export interface PinGlyph {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly source: string;
  readonly viewBox: string;
  readonly style: 'fill' | 'stroke';
  readonly elements: readonly PinGlyphElement[];
}

/** Filled vertical pin — closest to WhatsApp conversation list. */
export const DEFAULT_PIN_GLYPH_ID = 'material-push-pin' as const;

/** Verified pushpin / pin glyphs — not hand-drawn approximations. */
export const PIN_GLYPHS: readonly PinGlyph[] = [
  {
    id: 'lucide-pin',
    label: 'Lucide pin',
    description: 'Board pushpin with round head and needle (current Lucide).',
    source: 'Lucide Icons (ISC)',
    viewBox: '0 0 24 24',
    style: 'stroke',
    elements: [
      { kind: 'line', x1: 12, y1: 17, x2: 12, y2: 22 },
      {
        kind: 'path',
        d: 'M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z',
      },
    ],
  },
  {
    id: 'material-push-pin',
    label: 'Material push pin',
    description: 'Vertical filled pin — close to WhatsApp chat list pins.',
    source: 'Google Material Symbols (Apache-2.0)',
    viewBox: '0 0 24 24',
    style: 'fill',
    elements: [
      {
        kind: 'path',
        d: 'm16 12 2 2v2h-5v6l-1 1-1-1v-6H6v-2l2-2V5H7V3h10v2h-1Zm-7.15 2h6.3L14 12.85V5h-4v7.85ZM12 14Z',
      },
    ],
  },
  {
    id: 'bootstrap-pin-fill',
    label: 'Bootstrap pin (filled)',
    description: 'Classic thumbtack silhouette, filled.',
    source: 'Bootstrap Icons (MIT)',
    viewBox: '0 0 16 16',
    style: 'fill',
    elements: [
      {
        kind: 'path',
        d: 'M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354',
      },
    ],
  },
  {
    id: 'bootstrap-pin-outline',
    label: 'Bootstrap pin (outline)',
    description: 'Same thumbtack as filled, stroked body.',
    source: 'Bootstrap Icons (MIT)',
    viewBox: '0 0 16 16',
    style: 'stroke',
    elements: [
      {
        kind: 'path',
        d: 'M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354m1.58 1.408-.002-.001zm-.002-.001.002.001A.5.5 0 0 1 6 2v5a.5.5 0 0 1-.276.447h-.002l-.012.007-.054.03a5 5 0 0 0-.827.58c-.318.278-.585.596-.725.936h7.792c-.14-.34-.407-.658-.725-.936a5 5 0 0 0-.881-.61l-.012-.006h-.002A.5.5 0 0 1 10 7V2a.5.5 0 0 1 .295-.458 1.8 1.8 0 0 0 .351-.271c.08-.08.155-.17.214-.271H5.14q.091.15.214.271a1.8 1.8 0 0 0 .37.282',
      },
    ],
  },
  {
    id: 'tabler-pin-filled',
    label: 'Tabler pin (filled)',
    description: 'Angled pushpin — bold filled shape.',
    source: 'Tabler Icons (MIT)',
    viewBox: '0 0 24 24',
    style: 'fill',
    elements: [
      {
        kind: 'path',
        d: 'M15.113 3.21l.094 .083l5.5 5.5a1 1 0 0 1 -1.175 1.59l-3.172 3.171l-1.424 3.797a1 1 0 0 1 -.158 .277l-.07 .08l-1.5 1.5a1 1 0 0 1 -1.32 .082l-.095 -.083l-2.793 -2.792l-3.793 3.792a1 1 0 0 1 -1.497 -1.32l.083 -.094l3.792 -3.793l-2.792 -2.793a1 1 0 0 1 -.083 -1.32l.083 -.094l1.5 -1.5a1 1 0 0 1 .258 -.187l.098 -.042l3.796 -1.425l3.171 -3.17a1 1 0 0 1 1.497 -1.26z',
      },
    ],
  },
  {
    id: 'lucide-map-pin',
    label: 'Lucide map pin',
    description: 'Location teardrop — wrong for “pinned chat”, shown for contrast.',
    source: 'Lucide Icons (ISC)',
    viewBox: '0 0 24 24',
    style: 'stroke',
    elements: [
      {
        kind: 'path',
        d: 'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
      },
      { kind: 'path', d: 'M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
    ],
  },
] as const;

export function getPinGlyph(id: string): PinGlyph | undefined {
  return PIN_GLYPHS.find((glyph) => glyph.id === id);
}

export function getDefaultPinGlyph(): PinGlyph {
  return getPinGlyph(DEFAULT_PIN_GLYPH_ID) ?? PIN_GLYPHS[0];
}
